import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { School, User, Building2, CreditCard, CheckCircle, ArrowLeft, ArrowRight, Sparkles, Shield, Users, BookOpen } from 'lucide-react'
import supabase from '../services/supabaseClient'

export default function SchoolRegistration() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [schoolCode, setSchoolCode] = useState('')

  const [formData, setFormData] = useState({
    schoolName: '',
    schoolAddress: '',
    schoolCity: '',
    schoolProvince: '',
    schoolPhone: '',
    schoolEmail: '',
    schoolWebsite: '',
    principalName: '',
    principalEmail: '',
    principalPhone: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    password: '',
    confirmPassword: '',
    estimatedLearners: '50'
  })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const validateStep = () => {
    setError('')
    
    switch (step) {
      case 1:
        if (!formData.schoolName || !formData.schoolAddress || !formData.schoolCity) {
          setError('Please fill in all required fields')
          return false
        }
        break
      case 2:
        if (!formData.principalName || !formData.principalEmail) {
          setError('Please fill in all required fields')
          return false
        }
        break
      case 3:
        if (!formData.adminFirstName || !formData.adminLastName || !formData.adminEmail || !formData.password) {
          setError('Please fill in all required fields')
          return false
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match')
          return false
        }
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters')
          return false
        }
        break
      default:
        break
    }
    return true
  }

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  // Simple school-level pricing - all roles included
  const calculateMonthlyCost = () => {
    return 155 // Flat fee per school, all roles included
  }

  // Helper function to delay execution
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

  // Helper function to create user profile with retry logic
  async function createUserProfile(authId, schoolId, maxRetries = 5) {
    let lastError = null
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Wait a bit for auth user to be fully committed (increases with each retry)
        await delay(attempt * 500)
        
        const { error: userError } = await supabase
          .from('users')
          .insert({
            auth_id: authId,
            school_id: schoolId,
            email: formData.adminEmail,
            first_name: formData.adminFirstName,
            last_name: formData.adminLastName,
            phone: formData.adminPhone,
            role: 'school_admin',
            email_verified: false
          })

        if (!userError) {
          return { success: true, error: null }
        }
        
        lastError = userError
        console.log(`User profile creation attempt ${attempt} failed:`, userError.message)
        
        // If it's not a foreign key error, don't retry
        if (!userError.message.includes('foreign key') && !userError.message.includes('users_auth_id_fkey')) {
          return { success: false, error: userError }
        }
      } catch (err) {
        lastError = err
        console.log(`User profile creation attempt ${attempt} exception:`, err.message)
      }
    }
    
    return { success: false, error: lastError }
  }

  const handleSubmit = async () => {
    if (!validateStep()) return
    
    setLoading(true)
    setError('')

    try {
      // Step 1: Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.adminEmail,
        password: formData.password,
      })

      if (authError) throw authError
      if (!authData.user) throw new Error('Auth user creation failed - no user returned')

      console.log('Auth user created:', authData.user.id)

      // Step 2: Create school record
      const { data: schoolData, error: schoolError } = await supabase
        .from('schools')
        .insert({
          name: formData.schoolName,
          address: formData.schoolAddress,
          city: formData.schoolCity,
          province: formData.schoolProvince,
          phone: formData.schoolPhone,
          email: formData.schoolEmail,
          website: formData.schoolWebsite,
          principal_name: formData.principalName,
          principal_email: formData.principalEmail,
          principal_phone: formData.principalPhone,
          admin_name: `${formData.adminFirstName} ${formData.adminLastName}`,
          admin_email: formData.adminEmail,
          admin_phone: formData.adminPhone,
          subscription_plan: 'standard',
          subscription_status: 'trial',
          max_teachers: 50,
          max_students: 500,
          max_parents: 1000
        })
        .select()
        .single()

      if (schoolError) {
        console.error('School creation error:', schoolError)
        throw new Error(`Failed to create school: ${schoolError.message}`)
      }

      console.log('School created:', schoolData.id)

      // Step 3: Create user record with retry logic
      const { success, error: userError } = await createUserProfile(authData.user.id, schoolData.id)

      if (!success) {
        console.error('User profile creation failed after retries:', userError)
        
        // Try to clean up - delete the school since user profile failed
        try {
          await supabase.from('schools').delete().eq('id', schoolData.id)
          console.log('Cleaned up school record after user profile failure')
        } catch (cleanupError) {
          console.error('Failed to cleanup school:', cleanupError)
        }
        
        throw new Error(`Failed to create user profile: ${userError?.message || 'Unknown error'}. Please try again.`)
      }

      console.log('User profile created successfully')
      setSchoolCode(schoolData.code)
      setSuccess(true)
    } catch (err) {
      console.error('Registration error:', err)
      setError(err.message || 'An error occurred during registration')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Registration Successful!
          </h2>
          <p className="text-gray-600 mb-8">
            Your school has been registered and your 14-day free trial has started.
          </p>
          
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8">
            <p className="text-sm text-gray-600 mb-2">Your School Code:</p>
            <p className="text-3xl font-bold text-teal-600">{schoolCode}</p>
            <p className="text-xs text-gray-500 mt-3">
              Save this code! Teachers and parents will need it to join your school.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors"
            >
              Sign In to Your Dashboard
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-2xl bg-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <School className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Register Your School
          </h1>
          <p className="mt-2 text-gray-600">
            Start your 14-day free trial - No credit card required
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                    s <= step
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <CheckCircle className="h-5 w-5" /> : s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-16 sm:w-24 h-1 ${
                      s < step ? 'bg-teal-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>School Info</span>
            <span>Principal</span>
            <span>Admin Account</span>
            <span>Plan</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}

          {/* Step 1: School Information */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Building2 className="h-5 w-5 mr-2 text-teal-600" />
                School Information
              </h2>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    School Name *
                  </label>
                  <input
                    type="text"
                    name="schoolName"
                    value={formData.schoolName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    placeholder="e.g., Sunshine Primary School"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    name="schoolAddress"
                    value={formData.schoolAddress}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    placeholder="Street address"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="schoolCity"
                      value={formData.schoolCity}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Province
                    </label>
                    <select
                      name="schoolProvince"
                      value={formData.schoolProvince}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    >
                      <option value="">Select Province</option>
                      <option value="Gauteng">Gauteng</option>
                      <option value="Western Cape">Western Cape</option>
                      <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                      <option value="Eastern Cape">Eastern Cape</option>
                      <option value="Free State">Free State</option>
                      <option value="Limpopo">Limpopo</option>
                      <option value="Mpumalanga">Mpumalanga</option>
                      <option value="North West">North West</option>
                      <option value="Northern Cape">Northern Cape</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School Phone
                    </label>
                    <input
                      type="tel"
                      name="schoolPhone"
                      value={formData.schoolPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School Email
                    </label>
                    <input
                      type="email"
                      name="schoolEmail"
                      value={formData.schoolEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website (optional)
                  </label>
                  <input
                    type="url"
                    name="schoolWebsite"
                    value={formData.schoolWebsite}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    placeholder="https://"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Principal Information */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <User className="h-5 w-5 mr-2 text-teal-600" />
                Principal Information
              </h2>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Principal's Full Name *
                  </label>
                  <input
                    type="text"
                    name="principalName"
                    value={formData.principalName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Principal's Email *
                  </label>
                  <input
                    type="email"
                    name="principalEmail"
                    value={formData.principalEmail}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Principal's Phone
                  </label>
                  <input
                    type="tel"
                    name="principalPhone"
                    value={formData.principalPhone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Admin Account */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Shield className="h-5 w-5 mr-2 text-teal-600" />
                Create Admin Account
              </h2>
              <p className="text-sm text-gray-600">
                This account will have full access to manage your school.
              </p>
              
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="adminFirstName"
                      value={formData.adminFirstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="adminLastName"
                      value={formData.adminLastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="adminEmail"
                    value={formData.adminEmail}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="adminPhone"
                    value={formData.adminPhone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                    placeholder="At least 8 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-900"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Plan Selection */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-teal-600" />
                Select Your Plan
              </h2>
              
              {/* Pricing Info Card */}
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-6 w-6 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Simple School Pricing</h3>
                </div>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between items-center py-2 border-b border-teal-100">
                    <span>One-time Setup Fee</span>
                    <span className="font-semibold text-gray-900">$310</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span>Monthly Subscription</span>
                    <span className="font-semibold text-gray-900">$155/month</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-teal-200">
                  <p className="text-sm text-teal-700 font-medium">✓ All roles included:</p>
                  <p className="text-sm text-gray-600">Principals, Teachers, Parents & Learners</p>
                </div>
              </div>

              {/* Monthly Cost Preview */}
              <div className="bg-gray-900 text-white rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-300">Your Monthly Cost:</span>
                  <span className="text-3xl font-bold text-teal-400">${calculateMonthlyCost()}/month</span>
                </div>
                <div className="text-sm text-gray-400 space-y-1">
                  <p>• Flat fee per school</p>
                  <p>• Unlimited users across all roles</p>
                </div>
              </div>

              {/* Trial Info */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">14-Day Free Trial</p>
                  <p className="text-sm text-green-700">No credit card required. After your trial, automatic billing starts at the rate shown above.</p>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="h-5 w-5 text-teal-600" />
                  <span>Unlimited principals & admins</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="h-5 w-5 text-teal-600" />
                  <span>Unlimited teachers & staff</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="h-5 w-5 text-teal-600" />
                  <span>Unlimited parents</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="h-5 w-5 text-teal-600" />
                  <span>Unlimited learners</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <BookOpen className="h-5 w-5 text-teal-600" />
                  <span>Full feature access</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <Shield className="h-5 w-5 text-teal-600" />
                  <span>Secure data storage</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span>24/7 support included</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span>No per-learner fees</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button
                onClick={handleBack}
                className="py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors flex items-center"
                disabled={loading}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/')}
                className="py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            )}

            {step < 4 ? (
              <button
                onClick={handleNext}
                className="py-3 px-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="py-3 px-6 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Complete Registration
                    <CheckCircle className="h-4 w-4 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <button onClick={() => navigate('/login')} className="text-teal-600 hover:text-teal-700 font-medium">
            Sign in
          </button>
        </p>
      </div>
    </div>
  )
}